import { useMutation, useQuery } from "@apollo/client/react";
import { throwForValidationResult } from "@/lib/graphql-error";
import { useIntl, type IntlShape } from "react-intl";
import {
  CreateExperienceDocument,
  DeleteExperienceDocument,
  MyselfExpertiseDataDocument,
  UpdateExperienceDocument,
  type MyselfExpertiseDataQuery,
} from "@/graphql/generated/operations";
import { recipientRelations, type ContentRecipient } from "@/lib/content-sharing";
import { toDateInstant } from "@/lib/date-instant";

export interface ExperienceFormInput {
  jobTitle: string;
  employer: string;
  department: string;
  location: string;
  startDate: string;
  endDate: string;
  // Optional fields surfaced for ProfilePage parity; left undefined on add()
  // means "don't touch" - the field isn't included in the payload.
  country?: string;
  industry?: string;
  activities?: readonly string[];
  sharedWith?: ReadonlyArray<ContentRecipient | string>;
  publiclyReadable?: boolean;
  allGroups?: boolean;
}

export type ExperienceItem = NonNullable<
  NonNullable<MyselfExpertiseDataQuery["myself"]>["experiences"]
>[number];

export interface UseExperiencesResult {
  items: readonly ExperienceItem[];
  add: (input: ExperienceFormInput) => Promise<void>;
  update: (id: string, input: ExperienceFormInput) => Promise<void>;
  remove: (id: string) => Promise<void>;
  busy: boolean;
  error: Error | null;
}

function toDataInput(input: ExperienceFormInput, intl: IntlShape) {
  const startDate = toDateInstant(input.startDate);
  if (!startDate) {
    throw new Error(
      intl.formatMessage({ id: "profile.page.error.startDateRequired", defaultMessage: "Start date is required." }),
    );
  }
  if (!input.jobTitle.trim()) {
    throw new Error(
      intl.formatMessage({ id: "profile.page.error.jobTitleRequired", defaultMessage: "Job title is required." }),
    );
  }
  return {
    jobTitle: input.jobTitle,
    employer: input.employer || null,
    department: input.department || null,
    location: input.location || null,
    startDate,
    endDate: toDateInstant(input.endDate),
    ...(input.country !== undefined
      ? { country: input.country ? { id: input.country } : null }
      : {}),
    ...(input.industry !== undefined
      ? { industry: input.industry ? { id: input.industry } : null }
      : {}),
    ...(input.activities !== undefined
      ? { activities: [...input.activities] }
      : {}),
    ...(input.allGroups !== undefined ? { allGroups: input.allGroups } : {}),
    ...(input.publiclyReadable !== undefined ? { publiclyReadable: input.publiclyReadable } : {}),
    ...(input.sharedWith !== undefined
      ? { sharedWith: recipientRelations(input.sharedWith) }
      : {}),
  };
}

export function useExperiences(): UseExperiencesResult {
  const intl = useIntl();
  const query = useQuery(MyselfExpertiseDataDocument);
  const personId = query.data?.myself?.id;

  const saveFailed = intl.formatMessage({ id: "profile.page.error.saveExperience", defaultMessage: "Could not save the experience" });

  const [createMut, createState] = useMutation(CreateExperienceDocument, {
    refetchQueries: [{ query: MyselfExpertiseDataDocument }],
  });
  const [updateMut, updateState] = useMutation(UpdateExperienceDocument, {
    refetchQueries: [{ query: MyselfExpertiseDataDocument }],
  });
  const [deleteMut, deleteState] = useMutation(DeleteExperienceDocument, {
    refetchQueries: [{ query: MyselfExpertiseDataDocument }],
  });

  async function add(input: ExperienceFormInput) {
    if (!personId) throw new Error("useExperiences: no personId yet");
    const created = await createMut({
      variables: {
        input: { data: { ...toDataInput(input, intl), person: { id: personId } } },
      },
    });
    // Both union members are selected, so a rejection resolves rather than throws. Fail-closed on
    // the success typename so an unexpected member is a failure, not a silent "saved".
    const out = created.data?.createExperience;
    if (out?.__typename !== "CreateExperienceResult") {
      throwForValidationResult(out, saveFailed);
    }
  }

  async function update(id: string, input: ExperienceFormInput) {
    const updated = await updateMut({ variables: { id, input: { data: toDataInput(input, intl) } } });
    // Both union members are selected, so a rejection resolves rather than throws. Fail-closed on
    // the success typename so an unexpected member is a failure, not a silent "saved".
    const out = updated.data?.updateExperience;
    if (out?.__typename !== "UpdateExperienceResult") {
      throwForValidationResult(out, saveFailed);
    }
  }

  async function remove(id: string) {
    await deleteMut({ variables: { input: { id } } });
  }

  return {
    items: query.data?.myself?.experiences ?? [],
    add,
    update,
    remove,
    busy: createState.loading || updateState.loading || deleteState.loading,
    error:
      (createState.error as Error | undefined) ??
      (updateState.error as Error | undefined) ??
      (deleteState.error as Error | undefined) ??
      null,
  };
}
